// ═══════════════════════════════════════════════════════════════════════════
// DOMAIN: rig paramétrique de la mascotte — config → SVG déterministe
// ═══════════════════════════════════════════════════════════════════════════
//
// Rendu 100 % déterministe (aucun token IA). Le composant volet injecte une
// seule fois les @keyframes (classes .head/.eyelid/.brow/.hand) ; ce module ne
// produit que le markup SVG. Personnalisable par tenant via `MascotAppearance`
// (cf. page de paramétrage). Défaut = « Professeur Ornaxion » charte Ornexia.

export type HairStyle = 'bald' | 'short' | 'wavy' | 'bun';
export type GlassesShape = 'round' | 'square' | 'none';
export type FacialHair = 'none' | 'mustache' | 'goatee' | 'beard';
export type NeckStyle = 'bowtie' | 'tie' | 'scarf';

export interface MascotAppearance {
  skin: string;
  hair: string;
  accent: string; // lunettes + accessoire de cou
  shirt: string;
  facialColor: string;
  hairStyle: HairStyle;
  glasses: GlassesShape;
  facial: FacialHair;
  neck: NeckStyle;
}

// « Professeur Ornaxion » — variante charte Ornexia (Navy + Teal).
export const DEFAULT_APPEARANCE: MascotAppearance = {
  skin: '#f2c39c',
  hair: '#c9ccd2',
  accent: '#1d9e75', // Teal accent
  shirt: '#1a3f6f', // Navy light
  facialColor: '#eceef1',
  hairStyle: 'bald',
  glasses: 'round',
  facial: 'beard',
  neck: 'bowtie',
};

const HAIR_STYLES: ReadonlySet<string> = new Set(['bald', 'short', 'wavy', 'bun']);
const GLASSES: ReadonlySet<string> = new Set(['round', 'square', 'none']);
const FACIALS: ReadonlySet<string> = new Set(['none', 'mustache', 'goatee', 'beard']);
const NECKS: ReadonlySet<string> = new Set(['bowtie', 'tie', 'scarf']);

/** N'accepte qu'une couleur hex #rgb/#rrggbb (la config admin est injectée
 * dans du SVG — on évite toute chaîne arbitraire). Sinon fallback. */
export function sanitizeColor(v: unknown, fallback: string): string {
  if (typeof v !== 'string') return fallback;
  const c = v.trim();
  return /^#[0-9a-fA-F]{6}$/.test(c) || /^#[0-9a-fA-F]{3}$/.test(c) ? c : fallback;
}

function pickEnum(v: unknown, allowed: ReadonlySet<string>, fallback: string): string {
  return typeof v === 'string' && allowed.has(v) ? v : fallback;
}

/** Fusionne une config partielle (JSONB tenant) avec les défauts, en validant. */
export function normalizeAppearance(raw: unknown): MascotAppearance {
  const a = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const d = DEFAULT_APPEARANCE;
  return {
    skin: sanitizeColor(a['skin'], d.skin),
    hair: sanitizeColor(a['hair'], d.hair),
    accent: sanitizeColor(a['accent'], d.accent),
    shirt: sanitizeColor(a['shirt'], d.shirt),
    facialColor: sanitizeColor(a['facialColor'], d.facialColor),
    hairStyle: pickEnum(a['hairStyle'], HAIR_STYLES, d.hairStyle) as HairStyle,
    glasses: pickEnum(a['glasses'], GLASSES, d.glasses) as GlassesShape,
    facial: pickEnum(a['facial'], FACIALS, d.facial) as FacialHair,
    neck: pickEnum(a['neck'], NECKS, d.neck) as NeckStyle,
  };
}

function hairTop(style: HairStyle, hair: string): string {
  if (style === 'bald')
    return `<path d="M52 74 Q66 58 100 60 Q134 58 148 74 Q120 66 100 67 Q80 66 52 74 Z" fill="${hair}"/>`;
  if (style === 'short')
    return `<path d="M44 110 Q40 52 100 52 Q160 52 156 110 L156 90 Q150 72 100 70 Q50 72 44 90 Z" fill="${hair}"/>`;
  if (style === 'wavy')
    return `<path d="M42 112 Q40 50 100 50 Q160 50 158 112 L158 90 Q150 72 138 84 Q126 72 112 84 Q100 72 88 84 Q74 72 62 84 Q50 72 42 90 Z" fill="${hair}"/>`;
  // bun
  return `<circle cx="100" cy="44" r="15" fill="${hair}"/><path d="M46 108 Q44 58 100 56 Q156 58 154 108 L154 92 Q148 78 100 76 Q52 78 46 92 Z" fill="${hair}"/>`;
}

function glassesSvg(shape: GlassesShape, color: string): string {
  if (shape === 'round')
    return `<g fill="none" stroke="${color}" stroke-width="3.5"><circle cx="78" cy="110" r="17"/><circle cx="122" cy="110" r="17"/><line x1="95" y1="108" x2="105" y2="108"/><line x1="61" y1="106" x2="50" y2="102"/><line x1="139" y1="106" x2="150" y2="102"/></g>`;
  if (shape === 'square')
    return `<g fill="none" stroke="${color}" stroke-width="3.5"><rect x="60" y="95" width="34" height="30" rx="4"/><rect x="106" y="95" width="34" height="30" rx="4"/><line x1="94" y1="108" x2="106" y2="108"/><line x1="60" y1="104" x2="50" y2="100"/><line x1="140" y1="104" x2="150" y2="100"/></g>`;
  return '';
}

function facialSvg(facial: FacialHair, color: string): string {
  const moustache = `<path d="M74 141 Q88 134 100 140 Q112 134 126 141 Q112 148 100 144 Q88 148 74 141 Z" fill="${color}"/>`;
  if (facial === 'mustache') return moustache;
  if (facial === 'goatee')
    return moustache + `<path d="M90 150 Q100 176 110 150 Q100 158 90 150 Z" fill="${color}"/>`;
  if (facial === 'beard')
    return (
      `<path d="M52 118 Q54 176 100 182 Q146 176 148 118 Q140 150 100 152 Q60 150 52 118 Z" fill="${color}"/>` +
      moustache
    );
  return '';
}

function neckSvg(neck: NeckStyle, accent: string): string {
  if (neck === 'bowtie')
    return `<path d="M100 188 L84 181 L84 197 Z" fill="${accent}"/><path d="M100 188 L116 181 L116 197 Z" fill="${accent}"/><circle cx="100" cy="189" r="5" fill="${accent}"/>`;
  if (neck === 'tie')
    return `<path d="M94 181 L106 181 L100 190 Z" fill="${accent}"/><path d="M96 189 L104 189 L107 222 L100 228 L93 222 Z" fill="${accent}"/>`;
  // scarf
  return `<path d="M76 183 Q100 197 124 183 L127 197 Q100 212 73 197 Z" fill="${accent}"/><path d="M116 196 L128 196 L124 220 L112 218 Z" fill="${accent}"/>`;
}

/**
 * Construit le SVG animé de la mascotte pour une config d'apparence.
 * Le SVG référence les classes .head/.eyelid/.brow/.hand — les @keyframes
 * sont injectées par le composant appelant.
 */
export function buildMascotSvg(raw?: unknown): string {
  const a = normalizeAppearance(raw);
  const s = a.skin;
  const head =
    `<g class="head">` +
    hairTop(a.hairStyle, a.hair) +
    `<circle cx="43" cy="114" r="8" fill="${s}"/><circle cx="157" cy="114" r="8" fill="${s}"/>` +
    `<circle cx="100" cy="108" r="60" fill="${s}"/>` +
    hairTop(a.hairStyle, a.hair) +
    `<rect class="brow" x="60" y="86" width="30" height="7" rx="3.5" fill="${a.facialColor}"/>` +
    `<rect class="brow" x="110" y="86" width="30" height="7" rx="3.5" fill="${a.facialColor}"/>` +
    `<circle cx="78" cy="110" r="13" fill="#fff"/><circle cx="122" cy="110" r="13" fill="#fff"/>` +
    `<circle cx="80" cy="112" r="5" fill="#2c2f36"/><circle cx="124" cy="112" r="5" fill="#2c2f36"/>` +
    `<circle class="eyelid" cx="78" cy="110" r="13" fill="${s}"/><circle class="eyelid" cx="122" cy="110" r="13" fill="${s}"/>` +
    glassesSvg(a.glasses, a.accent) +
    `<path d="M97 122 Q100 132 104 124" fill="none" stroke="#d09a6e" stroke-width="3" stroke-linecap="round"/>` +
    facialSvg(a.facial, a.facialColor) +
    `<path d="M88 157 Q100 163 112 157" fill="none" stroke="#c98f6a" stroke-width="2.5" stroke-linecap="round"/>` +
    `</g>`;
  return (
    `<svg viewBox="0 0 200 240" width="180" height="216" role="img" aria-label="Mascotte">` +
    `<ellipse cx="100" cy="214" rx="68" ry="11" fill="rgba(0,0,0,.09)"/>` +
    `<path d="M40 240 Q42 182 100 178 Q158 182 160 240 Z" fill="${a.shirt}"/>` +
    neckSvg(a.neck, a.accent) +
    `<g class="hand"><rect x="150" y="150" width="15" height="34" rx="7" fill="${s}"/><circle cx="157" cy="184" r="14" fill="${s}"/></g>` +
    head +
    `</svg>`
  );
}

/** CSS (keyframes + classes) à injecter une seule fois par le composant. */
export const MASCOT_KEYFRAMES = `
.orn-mascot .eyelid{transform-box:fill-box;transform-origin:center top;animation:ornBlink 5.2s infinite}
.orn-mascot .brow{transform-box:fill-box;transform-origin:center;animation:ornBrow 6s ease-in-out infinite}
.orn-mascot .head{transform-box:fill-box;transform-origin:center bottom;animation:ornBob 4.6s ease-in-out infinite}
.orn-mascot .hand{transform-box:fill-box;transform-origin:70% 90%;animation:ornPoint 3.4s ease-in-out infinite}
@keyframes ornBlink{0%,92%,100%{transform:scaleY(0)}94%,97%{transform:scaleY(1)}}
@keyframes ornBrow{0%,70%,100%{transform:translateY(0)}80%,88%{transform:translateY(-3px)}}
@keyframes ornBob{0%,100%{transform:translateY(0) rotate(0)}50%{transform:translateY(-3px) rotate(-1.2deg)}}
@keyframes ornPoint{0%,60%,100%{transform:rotate(0)}75%,85%{transform:rotate(-11deg)}}
@media (prefers-reduced-motion: reduce){.orn-mascot .eyelid,.orn-mascot .brow,.orn-mascot .head,.orn-mascot .hand{animation:none}}
`;
